import { useState } from "react";
import { differenceBy } from "lodash-es";
import {
  add,
  addDays,
  startOfDay,
  startOfISOWeek,
  startOfWeek,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  getDay,
  isSameDay,
  parseISO,
  isWithinInterval,
} from "date-fns";
import { Chevron, DayPicker, useDayPicker } from "react-day-picker";

import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Text,
  VStack,
  Tag,
  TagLabel,
  TagCloseButton,
  Show,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";

import { useController } from "react-hook-form";

import { maybePluralizeNoun } from "#app/utils/words.js";
import { useLocale } from "#app/context/LocaleContext.jsx";
import { formatTagDate } from "#app/utils/dateFormat.js";
import { getTripNightDates } from "#app/utils/tripPlannerForm.js";

function parseRestrictedPeriods(restrictedPeriods) {
  if (!restrictedPeriods?.length) return [];
  return restrictedPeriods.map((period) => ({
    from: parseISO(period.start),
    to: parseISO(period.end),
    reason: period.reason,
  }));
}

function isDateRestricted(date, parsedPeriods) {
  return parsedPeriods.some((period) =>
    isWithinInterval(date, { start: period.from, end: period.to }),
  );
}

/**
 * Props:
 *   tripNightCount — trip-planner mode only: the route's number of nights.
 *     The picked days are then start days, and the calendar shades the rest
 *     of each trip next to its start.
 */
export function SpecificDatesInput({
  name,
  helperText,
  formContext,
  max,
  maxDate,
  restrictedPeriods,
  tripNightCount,
  ...rest
}) {
  const locale = useLocale();
  const {
    control,
    formState: { errors },
    trigger,
  } = formContext;

  const {
    field: { value, onChange },
  } = useController({
    name,
    control,
    rules: {
      validate: {
        dates: (dates) => {
          if (dates.length === 0) {
            return "Please select at least one day";
          }
          if (dates.length > max) {
            return `You can select at most ${max} days. Please remove ${maybePluralizeNoun(
              dates.length - max,
              "day",
            )}`;
          }
        },
      },
    },
  });

  const isInvalid = errors[name];

  const parsedPeriods = parseRestrictedPeriods(restrictedPeriods);
  const disabledRanges = parsedPeriods.map((p) => ({ from: p.from, to: p.to }));
  const uniqueReasons = [
    ...new Set(parsedPeriods.map((p) => p.reason).filter(Boolean)),
  ];

  const removeDay = (index) => {
    value.splice(index, 1);
    onChange(value);
    trigger(name);
  };

  // Restricted days render as disabled buttons, which swallow click events
  // entirely — tapping one gives no feedback (PostHog showed the dates
  // section is the form's top rageclick + abandonment spot). Pointerdown
  // still fires on disabled elements, so catch it on the wrapper and point
  // the user at the restriction reasons below the calendar.
  const [restrictedNudged, setRestrictedNudged] = useState(false);
  const handleCalendarPointerDown = (event) => {
    if (event.target.closest?.(".rdp-day_restricted")) {
      setRestrictedNudged(true);
    }
  };

  const atMax = value.length >= max;
  // At the cap, react-day-picker would replace the whole selection with the
  // day just clicked, so lock every unselected day instead.
  const isUnselected = (day) => !value.some((picked) => isSameDay(picked, day));

  const today = new Date();
  const lastDate = maxDate ?? add(today, { years: 1 });

  const isTrip = tripNightCount !== undefined;
  const dayNoun = isTrip ? "start day" : "day";
  const tripNightDates = isTrip ? getTripNightDates(value, tripNightCount) : [];

  return (
    <FormControl isInvalid={isInvalid} isRequired {...rest}>
      <FormLabel>
        What days {isTrip ? "could" : "can"} you{" "}
        <Text as="strong">{isTrip ? "start" : "arrive"}</Text>?{" "}
        <Text as="span" color="subtle">
          ({value.length} selected)
        </Text>
      </FormLabel>
      <Box display="flex" onPointerDownCapture={handleCalendarPointerDown}>
        <Box
          as={DayPicker}
          mode="multiple"
          min={0}
          max={max}
          captionLayout="dropdown"
          defaultMonth={
            value.length > 0 ? [...value].sort((a, b) => a - b)[0] : undefined
          }
          selected={value}
          onSelect={(e) => {
            onChange(e);
            trigger(name);
          }}
          startMonth={today}
          endMonth={lastDate}
          disabled={[
            { before: today },
            { after: lastDate },
            ...disabledRanges,
            ...(atMax ? [isUnselected] : []),
          ]}
          modifiers={{
            restricted: disabledRanges,
            tripNight: tripNightDates,
          }}
          modifiersClassNames={{
            restricted: "rdp-day_restricted",
            tripNight: "rdp-day_trip-night",
          }}
          components={{
            Weekdays: createCustomWeekdays({
              name,
              trigger,
              value,
              onChange,
              parsedPeriods,
              max,
              minDate: startOfDay(today),
              maxDate: startOfDay(lastDate),
            }),
            Chevron: createCustomChevron({ value }),
          }}
        />
        <Show above="md">
          <VStack mt="44px" px={4} maxH="275px" overflowY="auto">
            {value.map((day, index) => (
              <Tag
                size="md"
                key={index}
                w="115px"
                justifyContent="space-between"
              >
                <TagLabel>{formatTagDate(day, locale)}</TagLabel>
                <TagCloseButton onClick={() => removeDay(index)} />
              </Tag>
            ))}
          </VStack>
        </Show>
      </Box>

      {/* On mobile the desktop tag column is hidden, leaving no visible
          summary of what's picked — and mobile is 2/3 of form traffic. */}
      {value.length > 0 && (
        <Show below="md">
          <Wrap mt={2} spacing={1}>
            {value.map((day, index) => (
              <WrapItem key={index}>
                <Tag size="md">
                  <TagLabel>{formatTagDate(day, locale)}</TagLabel>
                  <TagCloseButton onClick={() => removeDay(index)} />
                </Tag>
              </WrapItem>
            ))}
          </Wrap>
        </Show>
      )}

      <RestrictedPeriodsInfo
        reasons={uniqueReasons}
        emphasized={restrictedNudged}
      />

      {isInvalid ? (
        <FormErrorMessage>{isInvalid.message}</FormErrorMessage>
      ) : (
        <FormHelperText>
          {/* At the cap, the calendar disables every unselected day with no
              explanation — say why further clicks do nothing. */}
          {atMax && (
            <Text color="warning" fontWeight="semibold">
              {isTrip
                ? `That's the maximum of ${max} start days — remove one to pick a different day.`
                : `That's the maximum of ${max} days — remove a day to pick a different one.`}
            </Text>
          )}
          {value.length > 0 && (
            <Text>
              You've selected{" "}
              <strong>{maybePluralizeNoun(value.length, dayNoun)}</strong>
              {isTrip ? "." : " to watch."}{" "}
              <Button
                size="sm"
                variant="link"
                colorScheme="brand"
                onClick={() => onChange([])}
                data-testid="reset-specific-dates"
              >
                Reset
              </Button>
            </Text>
          )}
          {value.length === 0 && (
            <Text>
              Select up to{" "}
              <strong>
                {max} different {dayNoun}s
              </strong>
            </Text>
          )}
        </FormHelperText>
      )}
    </FormControl>
  );
}

function RestrictedPeriodsInfo({ reasons, emphasized }) {
  if (!reasons.length) return null;
  return (
    <>
      {emphasized && (
        <Text mt={2} fontSize="sm" fontWeight="semibold" color="warning">
          That date can't be selected:
        </Text>
      )}
      {reasons.map((reason) => (
        <Text
          key={reason}
          mt={emphasized ? 0 : 2}
          fontSize="sm"
          color="warning"
        >
          {reason}
        </Text>
      ))}
    </>
  );
}

function createCustomChevron(customProps) {
  return (ChevronProps) => <CustomChevron {...ChevronProps} {...customProps} />;
}

// The month navigation arrows turn the accent colour when selected dates lie
// beyond the displayed month in that direction. The dropdowns' down arrows
// keep react-day-picker's own chevron.
function CustomChevron({ value, orientation, ...props }) {
  const { months } = useDayPicker();

  if (orientation !== "left" && orientation !== "right") {
    return <Chevron orientation={orientation} {...props} />;
  }

  const currentMonth = months[0].date;
  const hasDateBeyond =
    orientation === "left"
      ? value.some((day) => day < startOfMonth(currentMonth))
      : value.some((day) => day > endOfMonth(currentMonth));

  return (
    <Box
      as="svg"
      width="16px"
      height="16px"
      viewBox="0 0 120 120"
      color={hasDateBeyond ? "accent" : "default"}
    >
      {orientation === "left" ? (
        <path
          d="M69.490332,3.34314575 C72.6145263,0.218951416 77.6798462,0.218951416 80.8040405,3.34314575 C83.8617626,6.40086786 83.9268205,11.3179931 80.9992143,14.4548388 L80.8040405,14.6568542 L35.461,60 L80.8040405,105.343146 C83.8617626,108.400868 83.9268205,113.317993 80.9992143,116.454839 L80.8040405,116.656854 C77.7463184,119.714576 72.8291931,119.779634 69.6923475,116.852028 L69.490332,116.656854 L18.490332,65.6568542 C15.4326099,62.5991321 15.367552,57.6820069 18.2951583,54.5451612 L18.490332,54.3431458 L69.490332,3.34314575 Z"
          fill="currentColor"
          fillRule="nonzero"
        ></path>
      ) : (
        <path
          d="M49.8040405,3.34314575 C46.6798462,0.218951416 41.6145263,0.218951416 38.490332,3.34314575 C35.4326099,6.40086786 35.367552,11.3179931 38.2951583,14.4548388 L38.490332,14.6568542 L83.8333725,60 L38.490332,105.343146 C35.4326099,108.400868 35.367552,113.317993 38.2951583,116.454839 L38.490332,116.656854 C41.5480541,119.714576 46.4651794,119.779634 49.602025,116.852028 L49.8040405,116.656854 L100.804041,65.6568542 C103.861763,62.5991321 103.926821,57.6820069 100.999214,54.5451612 L100.804041,54.3431458 L49.8040405,3.34314575 Z"
          fill="currentColor"
        ></path>
      )}
    </Box>
  );
}

function createCustomWeekdays(customProps) {
  return (WeekdaysProps) => (
    <CustomWeekdays {...WeekdaysProps} {...customProps} />
  );
}

// Each weekday header is a button that selects (or clears) every selectable
// such day in the displayed month. It renders its own <thead>: the stock one
// is aria-hidden, which would hide these buttons from screen readers.
function CustomWeekdays({
  className,
  style,
  name,
  trigger,
  value,
  onChange,
  parsedPeriods,
  max,
  minDate,
  maxDate,
}) {
  const {
    months,
    classNames,
    styles,
    formatters: { formatWeekdayName },
    labels: { labelWeekday },
    dayPickerProps: { locale, weekStartsOn, ISOWeek },
  } = useDayPicker();

  const weekdays = getWeekdays(locale, weekStartsOn, ISOWeek);

  const currentMonth = months[0].date;

  const startOfMonthDate = startOfMonth(currentMonth);
  const endOfMonthDate = endOfMonth(currentMonth);

  const daysInMonth = eachDayOfInterval({
    start: startOfMonthDate,
    end: endOfMonthDate,
  }).filter((day) => day >= minDate && day < maxDate);

  const handleWeekdayClick = (weekday) => {
    const dayOfWeek = getDay(weekday);

    const selectableDays = daysInMonth.filter(
      (day) =>
        getDay(day) === dayOfWeek &&
        !isDateRestricted(day, parsedPeriods || []),
    );

    const unselectedDays = differenceBy(selectableDays, value, (day) =>
      day.getTime(),
    );
    const isFullySelected = unselectedDays.length === 0;
    const isWithinMax = value.length < max;

    if (isFullySelected) {
      onChange(differenceBy(value, selectableDays, (day) => day.getTime()));
      trigger(name);
    } else if (isWithinMax) {
      onChange(unselectedDays.concat(value));
      trigger(name);
    }
  };

  return (
    <thead>
      <tr style={style} className={className}>
        {weekdays.map((weekday, i) => (
          <th
            key={i}
            scope="col"
            className={classNames.weekday}
            style={styles?.weekday}
            aria-label={labelWeekday(weekday, { locale })}
          >
            <Button size="sm" onClick={() => handleWeekdayClick(weekday)}>
              {formatWeekdayName(weekday, { locale })}
            </Button>
          </th>
        ))}
      </tr>
    </thead>
  );
}

/**
 * Generate a series of 7 days, starting from the week, to use for formatting
 * the weekday names (Monday, Tuesday, etc.).
 */
export function getWeekdays(
  locale,
  /** The index of the first day of the week (0 - Sunday). */
  weekStartsOn,
  /** Use ISOWeek instead of locale/ */
  ISOWeek,
) {
  const start = ISOWeek
    ? startOfISOWeek(new Date())
    : startOfWeek(new Date(), { locale, weekStartsOn });

  const days = [];
  for (let i = 0; i < 7; i++) {
    const day = addDays(start, i);
    days.push(day);
  }
  return days;
}
